import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { api } from '../lib/api';
import { toast } from 'sonner';

const passwordSchema = z.object({
  currentpassword: z.string().min(1, 'Current password is required.'),
  password: z.string().min(8, 'New password is required (min 8 characters).'),
  password2: z.string().min(1, 'Password confirmation is required.'),
}).refine((data) => data.password === data.password2, {
  message: 'Please enter the same password again.',
  path: ['password2'],
});

type PasswordForm = z.infer<typeof passwordSchema>;

export function ProfileSecurityPage() {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
  });

  const resetMutation = useMutation({
    mutationFn: (data: PasswordForm) =>
      api.post('/api/profile/resetPassword', {
        currentpassword: data.currentpassword,
        password: data.password,
        password2: data.password2,
      }),
    onSuccess: () => {
      toast.success('Your password has changed successfully.');
      reset();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to change password');
    },
  });

  async function onSubmit(data: PasswordForm) {
    resetMutation.mutate(data);
  }

  return (
    <div>
      {/* Header — matches Wappler: centered layout */}
      <div className="flex justify-center items-start mb-3">
        <div className="w-1/2">
          <h1 className="text-2xl font-bold mb-3">Change your Password</h1>
          <p className="font-semibold text-[#767676] mb-4">
            Enter your current password and a new one to change your password
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex justify-center items-start">
          <div className="w-1/2">
            <Card className="rounded-[20px]">
              <CardContent className="p-6">
                <div className="mb-3">
                  <label className="form-label text-sm mb-1">Current password</label>
                  <input
                    type="password"
                    className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                    placeholder="Enter your current password"
                    {...register('currentpassword')}
                  />
                  {errors.currentpassword && (
                    <p className="text-sm text-red-500 mt-1">{errors.currentpassword.message}</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label text-sm mb-1">New password</label>
                  <input
                    type="password"
                    className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                    placeholder="Enter your new password"
                    {...register('password')}
                  />
                  {errors.password && (
                    <p className="text-sm text-red-500 mt-1">{errors.password.message}</p>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label text-sm mb-1">Confirm password</label>
                  <input
                    type="password"
                    className="w-full h-[42px] rounded-[4px] border border-gray-300 px-3 text-sm"
                    placeholder="Confirm your new password"
                    {...register('password2')}
                  />
                  {errors.password2 && (
                    <p className="text-sm text-red-500 mt-1">{errors.password2.message}</p>
                  )}
                </div>

                <div className="mb-3">
                  <Button type="submit" disabled={resetMutation.isPending}>
                    {resetMutation.isPending ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}